import PhonePreviewHeaderImage from '@/assets/images/phone-preview-header.svg';
import PhonePreviewBodyImage from '@/assets/images/phone-preview-body.svg';
import { BoxInfo, ButtonDialog, FileUpload } from '@/components';
import Header from '@/core/components/header/header';
import { CardMessage } from '@/modules/customization/components/cardMessage/cardMessage';
import { CropImageModal } from '@/modules/customization/components/cropImageModal/cropImageModal';
import { ImagePreview } from '@/modules/customization/components/imagePreview/imagePreview';
import { PhonePreview } from '@/modules/customization/components/phonePreview/phonePreview';
import { addImageIntoContainer } from '@/modules/customization/utils/addImageIntoContainer';
import { LIMIT_CHARACTERS_FILE_NAME } from '@/modules/files/constants/files';
import ICONS from '@/shared/entities/constants/icons';
import React, { useMemo, useState } from 'react';
import Grid from 'vkit/lib/context/grid';

interface LogoCustomizationProps {
  savedFileUrl?: string | null;
  file: File | null;
  setFile: (value: File | null) => void;
  loading?: boolean;
  onRemoveFile?: () => void;
}

export const LogoCustomization: React.FC<LogoCustomizationProps> = ({
  savedFileUrl,
  file = null,
  setFile,
  loading,
  onRemoveFile,
}) => {
  const [openResizeImageModal, setOpenResizeImageModal] = useState(false);

  const fileUrl = useMemo(
    () => (file ? URL.createObjectURL(file) : savedFileUrl),
    [file, savedFileUrl],
  );

  return (
    <>
      <CropImageModal
        open={openResizeImageModal}
        setOpen={setOpenResizeImageModal}
        setFile={setFile}
        file={file}
        aspectRatio={3}
      />
      <Grid column gap={16}>
        <Header
          title='Logo'
          description='Carregue o logo da sua empresa. Ele será exibido na tela inicial do aplicativo.'
          icon={ICONS.SHIELD}>
          <Grid margin='18px 0 0'>
            <PhonePreview label='Onde o logo deve aparecer?'>
              <Grid column padding='16px 0 14px 13px' stretch>
                <img
                  src={PhonePreviewHeaderImage}
                  alt='Pré visualização cabeçalho do aplicativo'
                  style={{ width: 'calc(100% - 3px)', margin: '0 13px 14px -9px' }}
                />
                <ImagePreview width={120} height={30} title='Logo' fileUrl={fileUrl} />
                <img
                  src={PhonePreviewBodyImage}
                  alt='Pré visualização corpo do aplicativo'
                  style={{ width: '100%', marginTop: 14 }}
                />
              </Grid>
            </PhonePreview>
          </Grid>
        </Header>

        <BoxInfo
          loading={loading}
          margin={0}
          footerAlignContent='left'
          footer={
            fileUrl && (
              <ButtonDialog
                solo
                color='danger'
                text='Tem certeza que deseja excluir a imagem?'
                onClick={() => {
                  setFile(null);
                  onRemoveFile?.();
                }}
                label='Excluir imagem'
                iconColor='danger'
                icon={ICONS.TRASH}
                dialogTextIcon={ICONS.TRASH}
                noDialogHeader
                buttonYesLabel='Excluir'
                buttonNoLabel='Cancelar'
              />
            )
          }>
          <Grid column gap={16} stretch>
            <Grid gap={16}>
              <CardMessage
                icon={ICONS.CROP}
                color='warning'
                message='<b>Formato:</b> JPG ou PNG com fundo branco ou transparente<br /><b>Resolução miníma:</b> 360x120 pixels'
              />

              <CardMessage
                icon={ICONS.BULB}
                color='success'
                message='Recomendamos o uso de uma versão retangular<br />com proporção proximada de 3:1'
              />
            </Grid>

            {fileUrl ? (
              <Grid column gap={16} stretch>
                <BoxInfo elevation={0} inlineStyle={{ backgroundColor: 'white' }}>
                  <Grid alignContent='center' alignItems='center' margin='24px'>
                    <img src={fileUrl} alt={file?.name} style={{ maxWidth: '100%' }} />
                  </Grid>
                </BoxInfo>
              </Grid>
            ) : (
              <FileUpload
                name='files'
                title='Arraste seu logo aqui'
                description='ou selecione um arquivo do seu<br />computador'
                icon={null}
                onChange={async (_name: string, value: File) => {
                  const fileWithBackground = await addImageIntoContainer(value);
                  setFile(fileWithBackground);
                  setOpenResizeImageModal(true);
                }}
                onDelete={() => {
                  setFile(null);
                }}
                allowedExtensions={['png', 'jpg', 'jpeg']}
                maxCharacterLimit={LIMIT_CHARACTERS_FILE_NAME}
                required
                formValidatorErrors={{}}
              />
            )}
          </Grid>
        </BoxInfo>
      </Grid>
    </>
  );
};
